import { useEffect, useState } from "react";
import { useAuth } from "../contexts/AuthContext";
import { useLocation, Link, useNavigate } from "react-router-dom";
import { Logo } from "../components/Logo";
import { api, formatErr } from "../lib/api";
import { Lock, Mail, Loader2, ShieldCheck, KeyRound, ArrowLeft, CheckCircle2, AlertTriangle, ArrowRight, LogOut, UserCheck } from "lucide-react";

export default function Login() {
  const { user, login, logout } = useAuth();
  const loc = useLocation();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);
  const [recoveryOpen, setRecoveryOpen] = useState(false);
  // Snapshot only AFTER AuthContext finishes its initial check (user is no longer null).
  // null = checking, false = anonymous, object = authenticated.
  const [wasPreAuthed, setWasPreAuthed] = useState(null);
  useEffect(() => {
    if (wasPreAuthed === null && user !== null) {
      setWasPreAuthed(user && typeof user === "object");
    }
  }, [user, wasPreAuthed]);

  // Once a fresh login completes (user transitioned from anonymous → object), navigate them in.
  useEffect(() => {
    if (wasPreAuthed === false && user && typeof user === "object") {
      nav(loc.state?.from || "/quote-portal", { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, wasPreAuthed]);

  // While auth is still checking → show a tiny loading placeholder
  if (user === null || wasPreAuthed === null) {
    return (
      <div className="min-h-screen bg-[#F8FAFC] flex items-center justify-center" data-testid="login-loading">
        <Loader2 size={20} className="animate-spin text-[#254391]" />
      </div>
    );
  }

  // If user was already authenticated when they landed here → show "Continue / Logout" choice.
  if (wasPreAuthed === true && user && typeof user === "object") {
    return <AlreadyAuthenticated user={user} onContinue={() => nav(loc.state?.from || "/quote-portal", { replace: true })} onLogoutAndSwitch={async () => { await logout(); nav("/quote-portal/login", { replace: true }); window.location.reload(); }} />;
  }

  const submit = async (e) => {
    e.preventDefault();
    setErr(""); setLoading(true);
    const r = await login(email.trim().toLowerCase(), password);
    setLoading(false);
    if (!r.ok) setErr(r.error);
  };

  return (
    <div className="min-h-screen bg-[#F8FAFC] flex items-center justify-center px-4 relative overflow-hidden" data-testid="login-page">
      <div className="absolute inset-0 opacity-[0.03] pointer-events-none">
        <div className="absolute inset-0" style={{
          backgroundImage: "linear-gradient(#254391 1px, transparent 1px), linear-gradient(90deg, #254391 1px, transparent 1px)",
          backgroundSize: "32px 32px"
        }} />
      </div>
      <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-[#254391] via-[#fda500] to-[#254391]" />

      <div className="w-full max-w-md relative">
        <div className="bg-white rounded-xl shadow-xl border border-gray-100 overflow-hidden">
          <div className="px-10 pt-10 pb-6 text-center">
            <Logo height={70} />
            <div className="mt-3 text-[11px] tracking-[0.22em] uppercase text-[#fda500] font-semibold">
              Built for Access. Made with Care.
            </div>
          </div>

          <div className="px-10 pb-10">
            <div className="text-center mb-6">
              <h1 className="text-2xl font-bold text-[#0A1128] tracking-tight">Quote Portal Sign-In</h1>
              <p className="text-xs text-gray-500 mt-2 flex items-start justify-center gap-1.5 leading-relaxed">
                <ShieldCheck size={13} className="text-[#fda500] flex-shrink-0 mt-0.5" />
                Restricted to authorised Ezy Access Solutions staff, moderators and administrators.
              </p>
            </div>

            <form onSubmit={submit} className="space-y-4" data-testid="login-form">
              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1.5">EMAIL ADDRESS</label>
                <div className="relative">
                  <Mail size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                  <input
                    data-testid="login-email"
                    type="email"
                    autoComplete="email"
                    required
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="w-full pl-10 pr-3 py-2.5 border border-gray-300 rounded-md text-sm focus:border-[#254391] focus:ring-2 focus:ring-[#254391]/20 outline-none transition"
                    placeholder="you@ezyaccesssolutions.com.au"
                  />
                </div>
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1.5">PASSWORD</label>
                <div className="relative">
                  <Lock size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                  <input
                    data-testid="login-password"
                    type="password"
                    autoComplete="current-password"
                    required
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="w-full pl-10 pr-3 py-2.5 border border-gray-300 rounded-md text-sm focus:border-[#254391] focus:ring-2 focus:ring-[#254391]/20 outline-none transition"
                    placeholder="••••••••"
                  />
                </div>
              </div>

              {err && (
                <div data-testid="login-error" className="text-xs bg-red-50 border border-red-200 text-red-700 px-3 py-2 rounded-md">
                  {err}
                </div>
              )}

              <button
                data-testid="login-submit"
                disabled={loading}
                type="submit"
                className="w-full bg-[#fda500] hover:bg-[#e59500] text-[#0A1128] font-bold tracking-wide py-3 rounded-md flex items-center justify-center gap-2 transition shadow-md disabled:opacity-60"
              >
                {loading ? <><Loader2 size={16} className="animate-spin" /> Signing in…</> : "SIGN IN"}
              </button>
            </form>

            <div className="mt-5 flex items-center justify-between gap-3">
              <button
                type="button"
                onClick={() => setRecoveryOpen(true)}
                data-testid="forgot-admin-pw-link"
                className="text-xs text-[#254391] hover:text-[#fda500] font-medium flex items-center gap-1"
              >
                <KeyRound size={12} /> Forgot administrator password?
              </button>
              <Link to="/ramp-calculator" className="text-xs text-gray-500 hover:text-[#254391] font-medium flex items-center gap-1" data-testid="back-to-public-link">
                <ArrowLeft size={12} /> Public calculator
              </Link>
            </div>

            <div className="mt-6 pt-4 border-t border-gray-100 text-[10px] uppercase tracking-[0.18em] text-gray-400 text-center">
              Staff & moderators cannot reset their own password — contact an administrator.
            </div>
          </div>
        </div>

        <div className="mt-5 text-center text-xs text-gray-500">
          ezyaccess.com.au · Trouble signing in? Contact your administrator.
        </div>
      </div>

      {recoveryOpen && <RecoveryDialog onClose={() => setRecoveryOpen(false)} />}
    </div>
  );
}

function RecoveryDialog({ onClose }) {
  const [step, setStep] = useState(1); // 1=request, 2=verify
  const [email, setEmail] = useState("");
  const [code, setCode] = useState("");
  const [newPw, setNewPw] = useState("");
  const [confirmPw, setConfirmPw] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [info, setInfo] = useState("");

  const requestCode = async (e) => {
    e.preventDefault();
    setBusy(true); setErr(""); setInfo("");
    try {
      await api.post("/auth/recovery/request", { email: email.trim().toLowerCase() });
      setInfo("If your email is authorised, a 6-digit code has been sent. Check your inbox (and spam).");
      setStep(2);
    } catch (e) {
      setErr(formatErr(e.response?.data?.detail) || e.message);
    } finally { setBusy(false); }
  };

  const verifyAndReset = async (e) => {
    e.preventDefault();
    setErr("");
    if (newPw.length < 8) { setErr("Password must be at least 8 characters."); return; }
    if (newPw !== confirmPw) { setErr("Passwords do not match."); return; }
    setBusy(true);
    try {
      await api.post("/auth/recovery/verify", {
        email: email.trim().toLowerCase(),
        code: code.trim(),
        new_password: newPw,
      });
      setInfo("Password reset successfully. You can now sign in.");
      setTimeout(onClose, 1800);
    } catch (e) {
      setErr(formatErr(e.response?.data?.detail) || e.message);
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" onClick={onClose} data-testid="recovery-dialog">
      <div className="bg-white w-full max-w-md rounded-xl shadow-2xl overflow-hidden" onClick={(e) => e.stopPropagation()}>
        <div className="bg-[#0A1128] text-white px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <KeyRound size={16} className="text-[#fda500]" />
            <div className="font-bold text-sm uppercase tracking-wider">Administrator Password Recovery</div>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-white text-xl leading-none">×</button>
        </div>

        <div className="p-6">
          {step === 1 && (
            <form onSubmit={requestCode} className="space-y-4" data-testid="recovery-step-1">
              <p className="text-sm text-gray-600">Enter your administrator email. If authorised, we'll send a 6-digit recovery code by email (valid for 15 minutes).</p>
              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1.5 uppercase tracking-wider">Admin Email</label>
                <input
                  type="email" required value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full px-3 py-2.5 border border-gray-300 rounded-md text-sm focus:border-[#254391] focus:ring-2 focus:ring-[#254391]/20 outline-none"
                  placeholder="admin@ezyaccess.com"
                  data-testid="recovery-email-input"
                />
              </div>
              {err && <ErrBox>{err}</ErrBox>}
              <button type="submit" disabled={busy} data-testid="recovery-request-btn"
                className="w-full bg-[#254391] hover:bg-[#1E3675] disabled:opacity-50 text-white font-bold py-3 rounded-md flex items-center justify-center gap-2">
                {busy ? <><Loader2 size={14} className="animate-spin" /> Sending…</> : "Send Recovery Code"}
              </button>
            </form>
          )}

          {step === 2 && (
            <form onSubmit={verifyAndReset} className="space-y-4" data-testid="recovery-step-2">
              {info && <OkBox>{info}</OkBox>}
              <p className="text-sm text-gray-600">Enter the 6-digit code from your email and choose a new password (min 8 chars).</p>
              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1.5 uppercase tracking-wider">Verification Code</label>
                <input
                  type="text" required maxLength={6} value={code}
                  onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
                  className="w-full px-3 py-2.5 border border-gray-300 rounded-md text-center text-xl font-mono tracking-[0.4em] focus:border-[#254391] focus:ring-2 focus:ring-[#254391]/20 outline-none"
                  placeholder="000000"
                  data-testid="recovery-code-input"
                />
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1.5 uppercase tracking-wider">New Password</label>
                <input type="password" required minLength={8} value={newPw} onChange={(e) => setNewPw(e.target.value)} className="w-full px-3 py-2.5 border border-gray-300 rounded-md text-sm focus:border-[#254391] outline-none" data-testid="recovery-new-pw" />
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1.5 uppercase tracking-wider">Confirm Password</label>
                <input type="password" required minLength={8} value={confirmPw} onChange={(e) => setConfirmPw(e.target.value)} className="w-full px-3 py-2.5 border border-gray-300 rounded-md text-sm focus:border-[#254391] outline-none" data-testid="recovery-confirm-pw" />
              </div>
              {err && <ErrBox>{err}</ErrBox>}
              <div className="flex gap-2">
                <button type="button" onClick={() => setStep(1)} className="px-3 py-2.5 text-sm border border-gray-300 hover:border-[#254391] rounded-md">Back</button>
                <button type="submit" disabled={busy} data-testid="recovery-verify-btn" className="flex-1 bg-[#fda500] hover:bg-[#e59500] disabled:opacity-50 text-[#0A1128] font-bold py-2.5 rounded-md flex items-center justify-center gap-2">
                  {busy ? <><Loader2 size={14} className="animate-spin" /> Resetting…</> : "Reset Password"}
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}

function ErrBox({ children }) {
  return <div className="text-xs bg-red-50 border border-red-200 text-red-700 px-3 py-2 rounded-md flex items-start gap-2"><AlertTriangle size={13} className="mt-0.5 flex-shrink-0" /> {children}</div>;
}
function OkBox({ children }) {
  return <div className="text-xs bg-emerald-50 border border-emerald-200 text-emerald-800 px-3 py-2 rounded-md flex items-start gap-2"><CheckCircle2 size={13} className="mt-0.5 flex-shrink-0" /> {children}</div>;
}

function AlreadyAuthenticated({ user, onContinue, onLogoutAndSwitch }) {
  const [busy, setBusy] = useState(false);
  return (
    <div className="min-h-screen bg-[#F8FAFC] flex items-center justify-center px-4 relative overflow-hidden" data-testid="already-authenticated">
      <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-[#254391] via-[#fda500] to-[#254391]" />
      <div className="w-full max-w-md relative">
        <div className="bg-white rounded-xl shadow-xl border border-gray-100 overflow-hidden">
          <div className="px-10 pt-10 pb-6 text-center">
            <Logo height={64} />
            <div className="mt-3 text-[11px] tracking-[0.22em] uppercase text-[#fda500] font-semibold">
              Built for Access. Made with Care.
            </div>
          </div>
          <div className="px-10 pb-10">
            <div className="text-center mb-6">
              <div className="w-12 h-12 mx-auto rounded-full bg-emerald-100 text-emerald-700 flex items-center justify-center mb-3">
                <UserCheck size={22} />
              </div>
              <h1 className="text-xl font-bold text-[#0A1128] tracking-tight">You're already signed in</h1>
              <p className="text-xs text-gray-500 mt-2">
                Logged in as <span className="font-mono text-[#0A1128]">{user.email}</span> ({user.role.replace("_", " ")})
              </p>
            </div>

            <div className="space-y-3">
              <button
                type="button"
                onClick={onContinue}
                data-testid="continue-to-portal-btn"
                className="w-full bg-[#fda500] hover:bg-[#e59500] text-[#0A1128] font-bold tracking-wide py-3 rounded-md flex items-center justify-center gap-2 transition shadow-md"
              >
                Continue to Quote Portal <ArrowRight size={15} />
              </button>
              <button
                type="button"
                disabled={busy}
                onClick={async () => { setBusy(true); try { await onLogoutAndSwitch(); } finally { setBusy(false); } }}
                data-testid="logout-and-switch-btn"
                className="w-full border-2 border-[#254391] text-[#254391] hover:bg-[#254391] hover:text-white font-semibold tracking-wide py-2.5 rounded-md flex items-center justify-center gap-2 transition disabled:opacity-60"
              >
                {busy ? <><Loader2 size={14} className="animate-spin" /> Logging out…</> : <><LogOut size={14} /> Logout and Login as Different User</>}
              </button>
            </div>

            <div className="mt-6 pt-4 border-t border-gray-100 flex items-center justify-between gap-3">
              <Link to="/ramp-calculator" className="text-xs text-gray-500 hover:text-[#254391] font-medium flex items-center gap-1">
                <ArrowLeft size={12} /> Public calculator
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

